import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Calendar, TrendingUp, Eye, Plus, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { apiGet } from '../utils/apiRequest';
import Toast from './Toast';
import { withServerWakeup } from '../utils/serverWakeup';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const AnalysisHistory = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    // Helper to show toast
    const showToastNotification = (message, type) => {
      setToastType(type);
      setToastMessage(message);
      setShowToast(true);
    };

    const analysesApiCall = async () => {
      // Use apiGet which handles 401 and token refresh automatically
      const data = await apiGet('/api/individuals/analyses');
      
      const fetchedAnalyses = data.analyses || [];
      setAnalyses(fetchedAnalyses);
    };

    try {
      await withServerWakeup(analysesApiCall, showToastNotification);
    } catch (error) {
      console.error('Error fetching analyses:', error);
      setToastMessage('Could not load analyses. Please try again.');
      setToastType('error');
      setShowToast(true);
      setAnalyses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseToast = () => {
    setShowToast(false);
    setToastMessage('');
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 65) return 'bg-blue-100';
    if (score >= 50) return 'bg-yellow-100';
    if (score >= 35) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-800';
    if (score >= 65) return 'text-blue-800';
    if (score >= 50) return 'text-yellow-800';
    if (score >= 35) return 'text-orange-800';
    return 'text-red-800';
  };

  const getBandColor = (band) => {
    const colors = {
      'Excellent': 'text-green-800 bg-green-100',
      'Good': 'text-blue-800 bg-blue-100',
      'Needs Improvement': 'text-yellow-800 bg-yellow-100',
      'At Risk': 'text-orange-800 bg-orange-100',
      'Critical': 'text-red-800 bg-red-100'
    };
    return colors[band] || 'text-gray-800 bg-gray-100';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewReport = (analysis) => {
    if (analysis.analysis_id) {
      navigate(`/deep-analysis/${analysis.analysis_id}`);
    } else if (analysis.checkup_id) {
      navigate(`/results-checkup/${analysis.checkup_id}`);
    } else {
      setToastMessage('Full report view coming soon! You can see analysis details in your dashboard.');
      setToastType('info');
      setShowToast(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading your analysis history...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analysis History</h1>
          <p className="text-gray-600 mt-1">Track your financial health over time</p>
        </div>
        <Link
          to="/individual"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Start New Analysis
        </Link>
      </div>

      {/* History Stats */}
      {analyses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-full p-3">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Analyses</p>
                <p className="text-2xl font-bold text-gray-900">{analyses.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-green-100 rounded-full p-3">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Latest Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyses.length > 0 ? `${analyses[0].score}/100` : 'N/A'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-purple-100 rounded-full p-3">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Average Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyses.length > 0 
                    ? Math.round(analyses.reduce((sum, a) => sum + a.score, 0) / analyses.length)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analysis History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Your Reports</h2>
        </div>
        
        {analyses.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No analyses yet</h3>
            <p className="text-gray-500 mb-6">
              Upload your financial files to begin tracking your business health.
            </p>
            <Link
              to="/individual"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Upload Your First Analysis
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {analyses.map((analysis) => (
              <div key={analysis.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <div className={`flex items-center px-3 py-1 rounded-full ${getScoreBg(analysis.score)}`}>
                        <span className={`text-lg font-bold ${getScoreColor(analysis.score)}`}>
                          {analysis.score}/100
                        </span>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBandColor(analysis.band)}`}>
                        {analysis.band}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(analysis.date)}
                    </div>
                    
                    {analysis.overview && (
                      <p className="text-sm text-gray-700 mb-2">{analysis.overview}</p>
                    )}
                    
                    {analysis.file_names && analysis.file_names.length > 0 && (
                      <div className="flex items-center text-xs text-gray-500">
                        <FileText className="w-3 h-3 mr-1" />
                        Files analyzed: {analysis.file_names.join(', ')}
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-6">
                    <button
                      onClick={() => handleViewReport(analysis)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                      data-testid="view-report-button"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Report
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Getting Started Guide */}
      {analyses.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Getting Started</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-blue-800 mb-2">What to Upload</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Profit & Loss Statement</li>
                <li>• Balance Sheet</li>
                <li>• Bank Statements (optional)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-2">What You'll Get</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Financial Health Score (0-100)</li>
                <li>• Personalized Recommendations</li>
                <li>• Trend Analysis Over Time</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={handleCloseToast}
        duration={4000}
        position="bottom-right"
      />
    </div>
  );
};

export default AnalysisHistory;